import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction } from 'react-router';
import {
  selectTokenTimeStamp,
  updateAuthState,
} from 'redux/reducers/authReducer';
import { addSeconds, differenceInMilliseconds } from 'date-fns';
import { useEffect, useRef } from 'react';

import { useRefreshTokenMutation } from 'services/api/auth';
import {
  JWT_SESSION_SECONDS,
  REFRESH_AT_REMAINING_MS,
  REFRESH_BACKOFF_BASE_MS,
  REFRESH_BACKOFF_MAX_MS,
  SESSION_CHECK_INTERVAL_MS,
} from 'constants/session';
import { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import stringifyError from 'helper/stringifyError';
import logout from 'utils/logout';

const NETWORK_STATUSES = ['FETCH_ERROR', 'TIMEOUT_ERROR'];

// Por que o refresh não devolveu jwt. Só copia campos conhecidos: `error.meta.args` carrega o
// corpo da requisição (o refresh_token) e NUNCA pode ir para o Sentry.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function describeRefreshFailure(res: any): { kind: string, detail: string } {
  if (res?.error) {
    const { status, data, error } = res.error;
    return {
      kind: NETWORK_STATUSES.includes(String(status)) ? 'network' : 'http',
      detail: stringifyError({ status, data: data ?? error }),
    };
  }

  // Resposta 200 sem jwt: registra só o envelope, sem o conteúdo de `data` (tokens).
  return {
    kind: 'sem-jwt',
    detail: stringifyError({
      code: res?.data?.code,
      message: res?.data?.message,
      temData: Boolean(res?.data?.data),
    }),
  };
}

/**
 * Mantém a sessão viva durante o expediente (issue #1841).
 *
 * - Expiração calculada com a janela REAL do JWT (18h — ver constants/session);
 *   antes usava `state.auth.timeout` (3600 hardcoded do /login/, valor errado).
 * - Renova quando restar <50% da vida, no tick de 3 min E ao acordar a aba
 *   (visibilitychange) — aba que dormiu além do limiar renova na hora.
 * - Falha do refresh NUNCA desloga; entra em backoff exponencial (3 min → 30 min)
 *   e só reporta ao Sentry na primeira falha da sequência, para não gerar
 *   centenas de PUTs/eventos durante uma indisponibilidade do backend.
 * - Logout só quando o JWT está realmente vencido.
 */
export default function useTokenExpirationCheck(navigate: NavigateFunction) {
  const dispatch = useDispatch();
  const jwtIssuedAt = useSelector(selectTokenTimeStamp);
  const [refreshToken] = useRefreshTokenMutation();
  // Fora do effect: ele re-arma sempre que navigate/dispatch mudam e zerava o contador,
  // o que reportava a mesma falha ao Sentry de novo a cada re-arme.
  const falhasSeguidasRef = useRef(0);
  const proximaTentativaEmRef = useRef(0); // epoch ms; 0 = pode tentar agora

  useEffect(() => {
    if (!jwtIssuedAt) { // sem sessão: nada a checar
      falhasSeguidasRef.current = 0;
      proximaTentativaEmRef.current = 0;
      return undefined;
    }

    let refreshing = false;

    const check = async () => {
      const expirationDate = addSeconds(jwtIssuedAt * 1000, JWT_SESSION_SECONDS);
      const remaining = differenceInMilliseconds(expirationDate, new Date());

      if (remaining <= 0) {
        const alert = { title: '', message: 'Sua sessão expirou. Faça login novamente.', variant: 'warning' };
        logout(navigate, alert);
        return;
      }

      const emBackoff = Date.now() < proximaTentativaEmRef.current;

      if (remaining < REFRESH_AT_REMAINING_MS && !refreshing && !emBackoff) {
        refreshing = true;
        try {
          const res = await refreshToken();

          if ('data' in res && res.data?.data?.jwt) {
            falhasSeguidasRef.current = 0;
            proximaTentativaEmRef.current = 0;
            dispatch(updateAuthState({
              jwt: res.data.data.jwt,
              refreshToken: res.data.data.refresh_token,
            }));
            // jwt novo → selectTokenTimeStamp muda → effect re-arma com a nova base
          } else {
            falhasSeguidasRef.current += 1;
            proximaTentativaEmRef.current = Date.now() + Math.min(
              REFRESH_BACKOFF_BASE_MS * (2 ** (falhasSeguidasRef.current - 1)),
              REFRESH_BACKOFF_MAX_MS,
            );
            if (falhasSeguidasRef.current === 1) {
              const { kind, detail } = describeRefreshFailure(res);
              // `kind` separa rede (esperado, some sozinho) de resposta do servidor (bug).
              sendInterfaceErrorToSentry(
                'JWT REFRESH FAILED',
                'refresh-token não retornou um jwt novo',
                detail,
                { tags: { refresh_failure: kind }, fingerprint: ['interface', 'JWT REFRESH FAILED', kind] },
              );
            }
          }
        } finally {
          refreshing = false;
        }
      }
    };

    check();

    const intervalId = setInterval(check, SESSION_CHECK_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [jwtIssuedAt, navigate, dispatch, refreshToken]);
}
